import React, { useMemo, useState } from 'react'
import BigNumber from 'bignumber.js'
import { convertFromWei } from 'utils/formatBalances'
import useDealDepositors from 'hooks/useDealDepositors'
import useShortlistEntries from 'hooks/useShortlistEntries'
import { patchShortlistEntries } from 'submission/lib/shortlist-api'
import useToast from 'hooks/useToast'
import AddEntryModal from './AddEntryModal'
import DocumentsModal from './DocumentsModal'
import type {
  Depositor,
  EquitySubmission,
  PatchAction,
  ShortlistEntry,
  ShortlistEntryStatus,
  ShortlistRound,
} from './types'

type Props = {
  dealId: string
  roundId: number
  round: ShortlistRound | null
  mutateRounds: () => Promise<unknown>
}

const STATUS_LABEL: Record<ShortlistEntryStatus, string> = {
  pending: 'Pending',
  signed: 'Signed',
  expired: 'Expired',
  excluded: 'Excluded',
  manual_added: 'Manual',
}

const STATUS_CLASS: Record<ShortlistEntryStatus, string> = {
  pending: 'bg-blue-100 text-blue-800',
  signed: 'bg-green-100 text-green-800',
  expired: 'bg-slate-200 text-slate-700',
  excluded: 'bg-red-100 text-red-800 line-through',
  manual_added: 'bg-purple-100 text-purple-800',
}

const truncate = (s: string): string =>
  s.length <= 12 ? s : `${s.slice(0, 6)}…${s.slice(-4)}`

const Copy: React.FC<{ value: string }> = ({ value }) => (
  <button
    type="button"
    title={value}
    className="hover:underline"
    onClick={(e) => {
      e.stopPropagation()
      navigator.clipboard?.writeText(value)
    }}
  >
    {truncate(value)}
  </button>
)

type PendingChange =
  | {
      type: 'exclude'
      privy_id: string
    }
  | {
      type: 'manual_add'
      privy_id: string
      wallet_address: string
      deposit_amount?: string
    }

type Candidate = {
  depositor: Depositor
  entry: ShortlistEntry | null
}

const hasDocs = (entry: ShortlistEntry | null): boolean => {
  const keys = entry?.submission?.data?.documentKeys
  return Array.isArray(keys) && keys.length > 0
}

const EntriesTable: React.FC<Props> = ({
  dealId,
  roundId,
  round,
  mutateRounds: _mutateRounds,
}) => {
  const isOpen = round?.status === 'open'
  const { showToast } = useToast()
  const [pending, setPending] = useState<Record<string, PendingChange>>({})
  const dirty = Object.keys(pending).length > 0

  const { depositors, mutate: mutateDepositors } = useDealDepositors(dealId, {
    paused: dirty,
  })
  const { entries, isLoading, isError, mutate } = useShortlistEntries(
    dealId,
    roundId,
    { paused: dirty },
  )
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [docsSubmission, setDocsSubmission] = useState<EquitySubmission | null>(
    null,
  )
  const [saving, setSaving] = useState(false)

  // Merge depositors + entries by privy_id, preserving depositor order (rank).
  const candidates: Candidate[] = useMemo(() => {
    const byPrivy = new Map<string, ShortlistEntry>()
    for (const e of entries) byPrivy.set(e.privy_id, e)
    return depositors.map((d) => ({
      depositor: d,
      entry: byPrivy.get(d.privy_id) ?? null,
    }))
  }, [depositors, entries])

  // Tick state derived from candidate + pending buffer.
  const isTicked = (c: Candidate): boolean => {
    const queuedExclude = !!pending[`exclude:${c.depositor.privy_id}`]
    const queuedManualAdd = !!pending[`manual_add:${c.depositor.privy_id}`]
    if (queuedExclude) return false
    if (queuedManualAdd) return true
    if (!c.entry) return false
    return c.entry.status === 'pending' || c.entry.status === 'manual_added'
  }

  const isCheckboxDisabled = (c: Candidate): boolean => {
    if (!isOpen) return true
    if (c.entry) {
      // Read-only once signed/expired/excluded.
      if (
        c.entry.status === 'signed' ||
        c.entry.status === 'expired' ||
        c.entry.status === 'excluded'
      ) {
        return true
      }
      return false
    }
    // No entry yet — eligible only if depositor has a Privy account.
    return !c.depositor.has_privy_account
  }

  const checkboxTooltip = (c: Candidate): string | undefined => {
    if (!c.entry && !c.depositor.has_privy_account) {
      return 'No Privy account; cannot notify'
    }
    if (c.entry?.status === 'signed') return 'Already signed (read-only)'
    if (c.entry?.status === 'expired') return 'Expired (read-only)'
    if (c.entry?.status === 'excluded') return 'Excluded (read-only)'
    return undefined
  }

  const queueExclude = (c: Candidate) => {
    setPending((p) => {
      const next = { ...p }
      delete next[`manual_add:${c.depositor.privy_id}`]
      next[`exclude:${c.depositor.privy_id}`] = {
        type: 'exclude',
        privy_id: c.depositor.privy_id,
      }
      return next
    })
  }

  const queueManualAdd = (c: Candidate) => {
    setPending((p) => {
      const next = { ...p }
      delete next[`exclude:${c.depositor.privy_id}`]
      next[`manual_add:${c.depositor.privy_id}`] = {
        type: 'manual_add',
        privy_id: c.depositor.privy_id,
        wallet_address: c.depositor.wallet_address,
        deposit_amount: c.depositor.deposit_amount,
      }
      return next
    })
  }

  const onTick = (c: Candidate, ticked: boolean) => {
    if (isCheckboxDisabled(c)) return
    if (ticked) {
      // Becoming ticked.
      if (c.entry) {
        // Was an entry pending exclude — undo by removing the exclude queue entry.
        setPending((p) => {
          const next = { ...p }
          delete next[`exclude:${c.depositor.privy_id}`]
          return next
        })
      } else {
        queueManualAdd(c)
      }
    } else {
      // Becoming unticked.
      if (c.entry) {
        queueExclude(c)
      } else {
        // Was queued manual_add — undo by removing.
        setPending((p) => {
          const next = { ...p }
          delete next[`manual_add:${c.depositor.privy_id}`]
          return next
        })
      }
    }
  }

  const onTickAll = () => {
    setPending((p) => {
      const next = { ...p }
      for (const c of candidates) {
        if (isCheckboxDisabled(c)) continue
        const queuedExclude = !!p[`exclude:${c.depositor.privy_id}`]
        const queuedManualAdd = !!p[`manual_add:${c.depositor.privy_id}`]
        const ticked = queuedExclude
          ? false
          : queuedManualAdd
          ? true
          : !!c.entry &&
            (c.entry.status === 'pending' || c.entry.status === 'manual_added')
        if (ticked) continue
        if (c.entry) {
          delete next[`exclude:${c.depositor.privy_id}`]
        } else {
          next[`manual_add:${c.depositor.privy_id}`] = {
            type: 'manual_add',
            privy_id: c.depositor.privy_id,
            wallet_address: c.depositor.wallet_address,
            deposit_amount: c.depositor.deposit_amount,
          }
        }
      }
      return next
    })
  }

  const onClearAll = () => {
    setPending((p) => {
      const next = { ...p }
      for (const c of candidates) {
        if (isCheckboxDisabled(c)) continue
        const queuedExclude = !!p[`exclude:${c.depositor.privy_id}`]
        const queuedManualAdd = !!p[`manual_add:${c.depositor.privy_id}`]
        const ticked = queuedExclude
          ? false
          : queuedManualAdd
          ? true
          : !!c.entry &&
            (c.entry.status === 'pending' || c.entry.status === 'manual_added')
        if (!ticked) continue
        if (c.entry) {
          next[`exclude:${c.depositor.privy_id}`] = {
            type: 'exclude',
            privy_id: c.depositor.privy_id,
          }
        } else {
          delete next[`manual_add:${c.depositor.privy_id}`]
        }
      }
      return next
    })
  }

  const discard = () => setPending({})

  const save = async () => {
    if (!dirty) return
    setSaving(true)
    try {
      const actions: PatchAction[] = Object.values(pending).map((c) =>
        c.type === 'exclude'
          ? { privy_id: c.privy_id, action: 'exclude' }
          : {
              privy_id: c.privy_id,
              action: 'manual_add',
              wallet_address: c.wallet_address,
              deposit_amount: c.deposit_amount,
            },
      )
      await patchShortlistEntries(dealId, roundId, actions)
      setPending({})
      await mutate()
      showToast({ type: 'success', message: 'Changes saved' })
    } catch (e: any) {
      showToast({
        type: 'error',
        message: `Save failed: ${e?.message ?? 'unknown'}`,
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <section
      data-testid="entries-table"
      className="p-4 bg-white rounded border border-slate-200"
    >
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-medium text-slate-900">
          Candidates — Round {round?.round_number ?? roundId}
        </h2>
        <div className="flex gap-2">
          {isOpen && (
            <>
              <button
                type="button"
                className="px-3 py-1 border rounded text-sm"
                onClick={onTickAll}
              >
                Select all eligible
              </button>
              <button
                type="button"
                className="px-3 py-1 border rounded text-sm"
                onClick={onClearAll}
              >
                Clear all
              </button>
              <button
                type="button"
                className="px-3 py-1 border rounded text-sm"
                onClick={() => setAddModalOpen(true)}
              >
                Add wallet (off-list)
              </button>
            </>
          )}
        </div>
      </div>

      {dirty && (
        <div className="sticky top-0 z-10 mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded flex items-center justify-between">
          <span className="text-sm text-yellow-900">
            {Object.keys(pending).length} unsaved change(s)
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              className="px-3 py-1 border rounded"
              onClick={discard}
              disabled={saving}
            >
              Discard
            </button>
            <button
              type="button"
              className="px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-50"
              onClick={save}
              disabled={saving}
            >
              {saving
                ? 'Saving…'
                : `Save ${Object.keys(pending).length} change(s)`}
            </button>
          </div>
        </div>
      )}

      {isLoading && <p className="text-slate-500 text-sm">Loading…</p>}
      {isError && (
        <p className="text-red-600 text-sm">Failed to load entries.</p>
      )}
      {!isLoading && !isError && candidates.length === 0 && (
        <p className="text-slate-500 text-sm">No depositors yet.</p>
      )}

      {candidates.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-left text-slate-600">
            <tr>
              <th className="py-1 w-8" />
              <th>Rank</th>
              <th>Wallet</th>
              <th>Privy ID</th>
              <th>Deposit</th>
              <th>Status</th>
              <th>Documents</th>
              <th>Notified</th>
              <th>Signed</th>
            </tr>
          </thead>
          <tbody>
            {candidates.map((c) => {
              const ticked = isTicked(c)
              const disabled = isCheckboxDisabled(c)
              const tip = checkboxTooltip(c)
              const isUnsaved =
                !!pending[`exclude:${c.depositor.privy_id}`] ||
                !!pending[`manual_add:${c.depositor.privy_id}`]
              return (
                <tr
                  key={c.depositor.privy_id}
                  className={`border-t ${isUnsaved ? 'bg-yellow-50' : ''}`}
                >
                  <td className="py-1">
                    <input
                      type="checkbox"
                      checked={ticked}
                      disabled={disabled}
                      title={tip}
                      onChange={(ev) => onTick(c, ev.target.checked)}
                    />
                  </td>
                  <td className="py-1">{c.entry?.rank ?? c.depositor.rank}</td>
                  <td>
                    <Copy value={c.depositor.wallet_address} />
                  </td>
                  <td>
                    <Copy value={c.depositor.privy_id} />
                  </td>
                  <td>
                    {convertFromWei(
                      new BigNumber(c.depositor.deposit_amount),
                    ).toString()}
                  </td>
                  <td>
                    {c.entry ? (
                      <span
                        className={`px-2 py-0.5 rounded text-xs ${
                          STATUS_CLASS[c.entry.status]
                        }`}
                      >
                        {STATUS_LABEL[c.entry.status]}
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                    {isUnsaved && (
                      <span className="ml-2 text-xs text-yellow-700">
                        (unsaved)
                      </span>
                    )}
                  </td>
                  <td>
                    {hasDocs(c.entry) ? (
                      <button
                        type="button"
                        className="px-2 py-0.5 border rounded text-xs"
                        onClick={() =>
                          setDocsSubmission(c.entry?.submission ?? null)
                        }
                      >
                        View docs
                      </button>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td>
                    {c.entry?.notified_at
                      ? new Date(c.entry.notified_at).toLocaleString()
                      : '—'}
                  </td>
                  <td>
                    {c.entry?.signed_at
                      ? new Date(c.entry.signed_at).toLocaleString()
                      : '—'}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      <AddEntryModal
        open={addModalOpen}
        dealId={dealId}
        roundId={roundId}
        onClose={() => setAddModalOpen(false)}
        onAdded={() => {
          mutate()
          mutateDepositors()
        }}
      />

      <DocumentsModal
        open={docsSubmission !== null}
        dealId={dealId}
        submission={docsSubmission}
        onClose={() => setDocsSubmission(null)}
      />
    </section>
  )
}

export default EntriesTable
